// Bootstrap antes do React montar.
// Aplica tier instantâneo + spatial nav singleton + lazy load i18n bundle.

import { getCapability, initSignals } from '@app-iptv/device'
import { setupI18n } from '@app-iptv/i18n'
import { initSpatialNavigation, setKeyboardMode } from '@app-iptv/ui'

import { setOnAuthInvalid } from './api'
import { getTokens } from './auth-store'
import { runAutoAdvanceStampMigration } from './autoadvance-stamp-migration'
import { curarCotaSePreciso } from './catalog-auto-cura'
import { registerCatalogCacheGuard } from './catalog-cache-guard'
import { bindContextOwner, reconcileContextOwner } from './context-owner'
import { concluirLimparDadosPendente } from './hard-reset-pendente'
import { runLowPriority } from './net-priority'
import { track } from './qoe'
import { registerSettingsScopeSync } from './settings-store'
import { useSessionStore } from './store'
import { getSurfaceOverride } from './surface'
import { bindFlushEvents, bindPullEvents, flushNow, hasSwipeSession, syncPull } from './sync'

let bootstrapped = false

/**
 * Marca (ou desmarca) `html[data-native-ts]` a partir da capability
 * `nativeTsPlayback` (classe webOS gen 4-5 provada). O CSS do frame de vídeo
 * (LivePip) usa esse atributo pra tirar o border-radius/overflow onde o
 * punch-through do plano de HW o ignoraria (canto reto) — SÓ onde o nativo está
 * ligado. Nas stacks com mpegts o atributo fica ausente e o arredondado
 * permanece. Idempotente. É a MESMA fonte que decide o ramo native-ts do player.
 */
function syncNativeTsAttr(): void {
  if (getCapability('nativeTsPlayback')) {
    document.documentElement.setAttribute('data-native-ts', '')
  } else {
    document.documentElement.removeAttribute('data-native-ts')
  }
}

/**
 * Carimba a IDENTIDADE DO BUILD no `<html>`: versão + SHA do commit.
 *
 * Existe por causa de uma classe de erro que já custou um P0 mal diagnosticado.
 * Em 03/08/2026 dois relatórios de device contraditórios ("open/return
 * instantâneos" e "TODOS os canais indisponíveis") citaram o MESMO número de
 * build, 2.26.95 — número que nem estava commitado (`apps/tv/package.json`
 * seguia em 2.26.0; os bumps eram de working tree). Somado a "o webOS não
 * substitui o app na mesma versão", ninguém consegue dizer, hoje, qual código
 * rodava em cada medição. O veredito "o stage fixo não sustenta carga" foi
 * atribuído sem se saber o binário.
 *
 * Com o carimbo, qualquer sonda de device (`scripts/perf/*`) lê o SHA em runtime
 * e RECUSA emitir veredito se ele não bater com o worktree que a operadora acha
 * que está medindo. Custo: dois atributos. `__APP_GIT_SHA__`/`__APP_VERSION__`
 * são `define` do Vite (vite.config.ts) — viram literal no bundle.
 */
function stampBuildIdentity(): void {
  try {
    document.documentElement.setAttribute('data-app-version', __APP_VERSION__)
    document.documentElement.setAttribute('data-app-sha', __APP_GIT_SHA__)
  } catch {
    /* carimbo é diagnóstico: nunca pode derrubar o boot */
  }
}

export async function bootstrap(): Promise<void> {
  if (bootstrapped) return
  bootstrapped = true

  // 0. Identidade do build no <html>, antes de qualquer coisa: se o boot falhar
  //    adiante, o carimbo já está lá pra sonda/suporte dizer QUAL código é este.
  stampBuildIdentity()

  // 1. Tier conceitual no <html> — leitura inicial com signals síncronos
  //    (storage='unknown' fallback). CSS preset aplicado imediato pra
  //    evitar flash de tema padrão. Atualizado no passo 5 após initSignals
  //    resolver — caso raro mas real onde RAM signal só fica disponível
  //    após init (algumas TVs lazy-load deviceMemory).
  document.documentElement.dataset['tier'] = getCapability('cpuTier')
  // Ramo native-ts (webOS provado): leitura inicial com signals síncronos (o
  // chromiumVersion do UA já mapeia a geração da LG mesmo antes da versão nativa
  // resolver). Refinado no passo 5.5 após initSignals.
  syncNativeTsAttr()

  // 2. Spatial navigation singleton
  initSpatialNavigation({
    debug: false,
    visualDebug: false,
  })

  // 2.1 Modo do TECLADO VIRTUAL. Em TV real (Tizen/webOS/AndroidTV/Fire/VIDAA)
  //     o <Input> só abre o IME no OK — pousar o foco do D-pad apenas destaca
  //     o campo (senão o teclado da plataforma sobe sozinho e sequestra as
  //     teclas: o usuário fica preso no campo). Sem override aqui, o
  //     @app-iptv/ui já resolve isso sozinho pela plataforma.
  //
  //     Só o BYPASS EXPLÍCITO de QA (`?surface=tv|mobile`) sobrescreve: assim o
  //     modelo de TV é testável num browser de desktop. Detecção normal (browser
  //     largo → surface 'tv') NÃO vira 'deferred' — em browser não há IME modal
  //     e clicar no campo deve focar e digitar como sempre.
  const surfaceOverride = getSurfaceOverride()
  if (surfaceOverride !== null) {
    setKeyboardMode(surfaceOverride === 'tv' ? 'deferred' : 'immediate')
  }

  // 3. Settings re-hidratam quando scope muda (provider/perfil). Registrar
  //    ANTES de setupI18n (que é await) pra cobrir mudanças de scope que
  //    poderiam ocorrer enquanto i18n carrega — sem isso, primeira mudança
  //    é perdida (subscribe ainda não existia).
  registerSettingsScopeSync()

  // 3.1 Isolamento do cache de catálogo entre clientes (P0). Purga os
  //     baldes `iptv-catalog-*` do Cache Storage no logout e na troca de
  //     provedor. Registrado JUNTO do scope sync e pelo mesmo motivo:
  //     precisa existir antes de qualquer transição de sessão, senão a
  //     primeira troca passa sem purgar.
  registerCatalogCacheGuard()

  // 4. Inicializa signals do device em paralelo com i18n. Storage signal
  //    é async (~10-50ms); demais síncronos. Capabilities cacheadas com
  //    fallback (passo 1) invalidam automaticamente quando init termina
  //    (glue subscribeSignalsRefresh em capabilities.ts). React caps
  //    (useCapability) re-renderizam.
  //
  //    `.catch` defensivo: boot NÃO pode falhar por sinais opcionais.
  //    initSignals em si trata internamente (storage.ts try/catch), mas
  //    qualquer regressão futura captura aqui sem matar o app.
  const initSignalsPromise = initSignals().catch((err) => {
    console.warn('[bootstrap] initSignals failed, continuing with sync fallback', err)
    return null
  })

  // 5. i18n com lazy load do idioma detectado (bundle chega via dynamic import)
  await setupI18n()
  // Garante que signals estão prontos antes de seguir — capabilities
  // dependentes de storage (swImageMaxEntries) precisam do dado real.
  await initSignalsPromise

  // 5.5. Refresh `data-tier` após init — cobre edge case onde tier
  //      conceitual mudou pós-storage (raro mas possível com RAM signal
  //      lazy). CSS preset re-aplica via attribute selector.
  document.documentElement.dataset['tier'] = getCapability('cpuTier')
  // Refina o native-ts com a versão canônica da plataforma (sdkVersion resolvido
  // no initSignals) — casa 1:1 com o ramo do player, que também lê a capability.
  syncNativeTsAttr()

  // 5.6. "Limpar dados" que não apagou tudo da última vez: tenta de novo AGORA
  //      (hard-reset-pendente.ts). Tem que ser AQUI, antes do passo 8
  //      (`flushNow`/`syncPull` abrem o outbox) e antes do render (as telas
  //      abrem catálogo, fichas e local-db): é o único momento em que nenhum
  //      módulo tem conexão aberta, e sem `onblocked` no idb-native um delete
  //      com conexão aberta pendura. Sem registro (o boot normal), sai na hora.
  //      Com registro, tem prazo próprio (3 s). Nunca lança; o `.catch` é a
  //      garantia da fronteira — o boot não pode cair por isto.
  await concluirLimparDadosPendente().catch(() => [])

  // 5.7. AUTO-CURA da cota (catalog-auto-cura.ts): disjuntor do catálogo em
  //      `off` e o reset deste episódio ainda não feito → apaga o `iptv-catalog`,
  //      que mesmo vazio retinha a cota da ORIGEM inteira (medido na LG do dono:
  //      apagar devolveu a escrita a favoritos, progresso e outbox). AQUI, pela
  //      mesma razão do 5.6: antes do passo 8 abrir o outbox e antes do render,
  //      os escritores já pegam a cota de volta NESTE boot. Fora de `off` (o boot
  //      normal) sai na hora, sem tocar o IndexedDB. Uma vez por episódio, com
  //      prazo (3 s); nunca lança.
  await curarCotaSePreciso('boot').catch(() => 'erro')

  // 6. Hook de "sessão inválida": refresh falhou (401), 401 normal, ou
  //    403 com error=account_expired (plano Xtream vencido). Limpa store
  //    com a razão correta — `logout(reason)` preenche flashError (i18n
  //    key) que o RootRoute lê e a LoginScreen mostra. AuthGuard global
  //    cuida da navegação fora da rota raiz (telas autenticadas
  //    montadas redirecionam pra `/`).
  setOnAuthInvalid((reason) => {
    track({
      type: 'auth_invalidated',
      ts: Math.floor(Date.now() / 1000),
      reason,
      source: 'backend',
    })
    useSessionStore.getState().logout(reason)
  })

  // 7. Sanity check: se o store diz "logado" mas não há tokens, desloga
  //    silencioso. Usa reason 'user' (não 'session_expired') porque
  //    nesse cenário não temos evidência de que tokens foram invalidados
  //    pelo backend — pode ser dev clearing localStorage manualmente,
  //    storage corrompido, etc. Comportamento: preserva mode/providerCode
  //    pro RootRoute redirecionar pra LoginScreen com creds salvas
  //    pré-preenchidas (UX consistente com logout explícito do Settings).
  //
  //    GUARD `!hasSwipeSession()` (fix/tv-rebootstrap-session): quem prova
  //    "logado" na identidade I5+I6 é a SESSÃO SWIPE em disco, NÃO o token
  //    Xtream (mesma invariante do RootRoute e de session-rehydrate.test.tsx:
  //    "quem decide 'abre logado' não é o token Xtream, é a sessão SWIPE
  //    estar no disco"). Pra um usuário pareado por QR, o `iptv:tokens`
  //    Xtream é vestigial: `swipeAuthHeader()` usa o token Swipe e nunca toca
  //    no Xtream (I3+I4). Mas o handler genérico de 401 do api.ts chama
  //    `clearTokens()` (Xtream) em QUALQUER 401 de rota autenticada — mesmo
  //    quando a identidade ativa é a Swipe. Se isso acontecer durante a
  //    sessão e depois ocorrer um re-bootstrap (reload do sw-recovery/
  //    RecoveryBoundary por ChunkLoadError/SW error), este sanity check via
  //    o `iptv:tokens` sumido e DESLOGAVA um usuário Swipe válido — a
  //    sessão Swipe (JWT) sobrevivia, a sessão do provider caía pra login.
  //    Com o guard, só deslogamos a base LEGADA (Xtream sem sessão Swipe),
  //    onde o token realmente é a única prova de auth.
  const session = useSessionStore.getState()
  if (session.isAuthed && !getTokens() && !hasSwipeSession()) {
    session.logout('user')
  }

  // 7b. O contexto de conta do store é da conta da sessão Swipe, e de nenhuma outra
  //     (cybersec P1, 10/10/2026): o disco pode ter sessão de B (ou nenhuma, depois de
  //     um 401) com o perfil e a fonte de A. Confere ANTES de o React montar, e liga a
  //     mesma conferência a cada troca de identidade da sessão. Ver context-owner.ts.
  bindContextOwner()
  reconcileContextOwner()

  // 8. Sync da conta cross-device: liga os flushes/pulls oportunistas (aba
  //    escondida/visível, fechamento) e, se há conta Swipe logada, esvazia o
  //    outbox pendente E PUXA o delta do servidor ("continuar de onde parou"
  //    vindo de outro device). Best-effort, não bloqueia o boot.
  bindFlushEvents()
  bindPullEvents()
  if (hasSwipeSession()) {
    void flushNow()
    void syncPull()
  }

  // 9. Limpeza ÚNICA dos carimbos do auto-advance em cascata (27/09/2026, decisão
  //    do dono). Aqui e não antes: os tombstones dela precisam saber se há sessão
  //    Swipe (lida no passo 8). Fora do caminho crítico (`runLowPriority` cede a
  //    vez ao primeiro frame). Depois de concluída é só uma leitura de
  //    localStorage. Best-effort: nunca derruba o boot.
  //    Ver autoadvance-stamp-migration.ts.
  void runLowPriority(() => runAutoAdvanceStampMigration()).catch((err: unknown) => {
    console.warn('[bootstrap] migração de carimbos falhou (tenta no próximo boot)', err)
  })

  // Long-session cleaner (#7 do roadmap pós-MVP) inicia em providers.tsx
  // — depende do QueryClient (RQ inactive query GC), que só existe após
  // React mount. NÃO mover pra cá sem refatorar o cleaner.
}

// MSW só em dev e quando não há backend real.
// Chamado de main.tsx em paralelo (não bloqueia render inicial).
export async function setupMockServer(): Promise<void> {
  if (!import.meta.env.DEV) return
  if (import.meta.env['VITE_API_BASE_URL']) return // backend real configurado
  try {
    const { worker } = await import('./mocks/browser')
    await worker.start({
      onUnhandledRequest: 'bypass',
      serviceWorker: { url: '/mockServiceWorker.js' },
    })
    console.warn('[mock] MSW ativo (Fase 1 — sem backend real)')
  } catch (err) {
    console.warn('[mock] failed to start', err)
  }
}
